import React, {
  FC,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import getYandexGeoData from "~/components/Forms/DealerPassportForm2/requests/getYandexGeoData";
import DynamicFormField from "~/components/Forms/DealerPassportForm2/UI/DynamicFormField";
import {
  CoordinatesContextProps,
  GetCoordinatesContext,
} from "~/components/Forms/DealerPassportForm2/getCoordinates";
import { useGeoPlaces } from "~/components/Forms/DealerPassportForm2/hook/useGeoPlaces";
import { GeoPlaceType } from "~/schemas/generated/graphql";

import { addressFields } from "./helper";

export interface IGeoFieldProps {
  prefix: string;
  dinamicPrefix?: string;
  field: string;
  type: GeoPlaceType;
  parentField: string | null;
  isRu: boolean;
  allDisabled: boolean;
}

const GeoField: FC<IGeoFieldProps> = ({
  prefix,
  dinamicPrefix,
  field,
  type,
  parentField,
  isRu,
  allDisabled,
}) => {
  const { t } = useTranslation();
  console.log(allDisabled, prefix);

  const [eventName, setEventName] = useState<string>(null);
  const { control, setValue, getValues } = useFormContext();
  const { currentCoordinates } = useContext<CoordinatesContextProps>(
    GetCoordinatesContext,
  );
  const parentValue = useWatch({
    control,
    name: parentField ? `${prefix}${parentField}` : undefined,
    defaultValue: undefined,
  })?.value;
  const isAutoFilling = useWatch({
    control,
    name: "isAutoFilling",
    defaultValue: false,
  });

  useEffect(() => {
    if (!isAutoFilling && eventName) {
      const childFields = getAllChildFields(
        prefix,
        eventName.replace(prefix, ""),
        addressFields,
      );
      setEventName(null);
      childFields.forEach((childField) => {
        setValue(childField, null, {
          shouldValidate: false,
          shouldDirty: false,
        });
      });
      setValue(`${prefix}index`, null, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [parentValue, prefix, setValue, isAutoFilling, addressFields, eventName]);

  const addressValues = useWatch({
    control,
    name: Object.keys(addressFields).map((f) => `${prefix}${f}`),
    defaultValue: Object.keys(addressFields).reduce(
      (acc, f) => ({
        ...acc,
        [`${prefix}${f}`]: null,
      }),
      {},
    ),
  });

  const fullAddress = useMemo(() => {
    if (
      prefix === "mainAddress" ||
      prefix === "legalAddress" ||
      prefix === "postAddress" ||
      prefix === "xwebSaleAddress" ||
      prefix === "xwebStoAddress" ||
      prefix === "ladaSaleAddress" ||
      prefix === "ladaStoAddress" ||
      prefix === dinamicPrefix
    ) {
      const index = getValues(`${prefix}index`)
        ? getValues(`${prefix}index`) + ", "
        : "";

      const addressParts = Object.keys(addressFields)
        .map((f) => addressValues[`${prefix}${f}`]?.label)
        .filter((part) => part && part !== "Россия");

      const officeValue =
        getValues(`${prefix}office`) || addressValues[`${prefix}office`];
      if (
        officeValue &&
        typeof officeValue === "string" &&
        officeValue.trim() !== ""
      ) {
        addressParts.push(officeValue.trim());
      }

      return addressParts.length > 0 ? index + addressParts.join(", ") : "";
    }
    return "";
  }, [addressValues, prefix, getValues]);

  useEffect(() => {
    if (
      prefix === "mainAddress" ||
      prefix === "legalAddress" ||
      prefix === "postAddress" ||
      prefix === "xwebSaleAddress" ||
      prefix === "xwebStoAddress" ||
      prefix === "ladaSaleAddress" ||
      prefix === "ladaStoAddress" ||
      prefix === dinamicPrefix
    ) {
      setValue(`${prefix}fullAddress`, fullAddress, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [fullAddress, setValue, prefix, dinamicPrefix]);

  useEffect(() => {
    if (type === "house") {
      const addressArray = fullAddress.split(", ");
      const indexCheck = Number(addressArray[0]);
      if (indexCheck && !isNaN(indexCheck)) {
        const fetchData = async () => {
          const result = await getYandexGeoData(fullAddress);
          const pos = result?.[0]?.GeoObject?.Point?.pos;
          if (!pos) return;
          const coor = pos.split(" ");
          if (coor.length < 2) return;
          currentCoordinates({
            title: prefix,
            latitude: coor[1],
            longitude: coor[0],
          });
        };
        fetchData();
      }
    }
  }, [type, fullAddress, prefix]);

  const exceptions = (field, prefix, parentValue) => {
    let parent = parentValue;
    if (
      (field === "district" ||
        field === "city" ||
        field === "locality" ||
        field === "street") &&
      getValues(`${prefix}subject`)
    ) {
      const exception = getValues(`${prefix}subject`).value;
      parent = exception;
    }
    if (
      (field === "locality" || field === "city" || field === "street") &&
      getValues(`${prefix}district`)
    ) {
      const exception = getValues(`${prefix}district`).value;
      parent = exception;
    }
    if (
      (field === "street" || field === "locality" || field === "house") &&
      getValues(`${prefix}city`)
    ) {
      const exception = getValues(`${prefix}city`).value;
      parent = exception;
    }
    if (
      (field === "street" || field === "house") &&
      getValues(`${prefix}locality`)
    ) {
      const exception = getValues(`${prefix}locality`).value;
      parent = exception;
    }
    if (field === "house" && getValues(`${prefix}street`)) {
      const exception = getValues(`${prefix}street`).value;
      parent = exception;
    }
    if (field === "office" && getValues(`${prefix}house`)) {
      const exception = getValues(`${prefix}office`);
      parent = exception;
    }

    return parent;
  };

  const { options, loading } = useGeoPlaces({
    type,
    parentId: exceptions(field, prefix, parentValue),
    prefix,
    isRu,
  });

  useEffect(() => {
    if (field === "house") {
      const selectedHouse = addressValues[`${prefix}${field}`];
      if (!selectedHouse) {
        setValue(`${prefix}index`, null, {
          shouldValidate: false,
          shouldDirty: false,
        });
        return;
      }

      const fullHouse = options.find(
        (opt) => opt.value === selectedHouse.value,
      );

      if (fullHouse?.indexAddress) {
        setValue(`${prefix}index`, fullHouse.indexAddress, {
          shouldValidate: false,
          shouldDirty: true,
        });
      } else {
        setValue(`${prefix}index`, null, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
    }
  }, [addressValues[`${prefix}${field}`], options]);

  const memoizedOptions = useMemo(() => options, [options]);

  return (
    <DynamicFormField
      name={`${prefix}${field}`}
      options={memoizedOptions}
      loading={loading}
      label={t(`dealers.${field}`)}
      type="select"
      getEventName={(name) => setEventName(name)}
      disabled={allDisabled}
    />
  );
};

export default memo(GeoField, (prevProps, nextProps) => {
  return (
    prevProps.prefix === nextProps.prefix &&
    prevProps.dinamicPrefix === nextProps.dinamicPrefix &&
    prevProps.field === nextProps.field &&
    prevProps.type === nextProps.type &&
    prevProps.parentField === nextProps.parentField &&
    prevProps.isRu === nextProps.isRu
  );
});

function getAllChildFields(
  prefix: string,
  field: string,
  addressFields: Record<
    string,
    {
      type: GeoPlaceType;
      parentField: string | null;
    }
  >,
) {
  const childFields = [];

  function findChildren(currentField) {
    Object.entries(addressFields).forEach(([childField, fieldConfig]) => {
      if (fieldConfig.parentField === currentField) {
        const prefixedChildField = `${prefix}${childField}`;
        childFields.push(prefixedChildField);
        findChildren(childField);
      }
    });
  }

  findChildren(field);
  return childFields;
}
